import { createContext, useContext, useEffect, useState } from "react";
import { getPlatformThemeById, applyPlatformTheme } from "@/lib/platform-themes";

type Theme = "light" | "dark";

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType>({
  theme: "light",
  toggleTheme: () => {},
});

function loadPlatformTheme(mode: Theme) {
  const isCustom = localStorage.getItem(`platform-custom-mode-${mode}`) === "true";
  const savedCustom = localStorage.getItem(`platform-custom-colors-${mode}`);
  const savedThemeId = localStorage.getItem("platform-theme") || "app-maisters";

  if (isCustom && savedCustom) {
    try {
      const colors = JSON.parse(savedCustom);
      applyPlatformTheme(colors, mode);
      return;
    } catch {}
  }

  const themeData = getPlatformThemeById(savedThemeId);
  const colors = mode === "dark" ? themeData.dark : themeData.light;
  applyPlatformTheme(colors, mode);
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    if (typeof window !== "undefined") {
      return (localStorage.getItem("theme") as Theme) || "light";
    }
    return "light";
  });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("light", "dark");
    root.classList.add(theme);
    localStorage.setItem("theme", theme);
    loadPlatformTheme(theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
